import moment from 'moment-timezone';
import { fetchWithRetry } from '../utils/fetchHelper';

/**
 * Helper para obtener el monto total de una transacción según el tipo de cuenta
 */
const getTransactionAmount = (t, accountType) => {
    return accountType === "Cuenta Actual" ? t.total_amount : t.total;
};

/**
 * Función genérica para obtener transacciones en un rango y calcular totales
 */ 
const fetchSummaryInRange = async (config) => {
    const { 
        accessToken, bpToken, accountType, 
        startMoment, endMoment, 
        limit, isV2 
    } = config;
    
    const apiUrl = process.env.REACT_APP_API_URL_DEV;
    let allTransactions = [];
    let currentPage = 1;
    let totalPages = 1;

    try {
        do {
            let requestUrl;
            let headers = { 'authorization': accessToken };

            if (isV2) {
                requestUrl = `${apiUrl}/api/v2/transactions?from=${startMoment.utc().format()}&to=${endMoment.utc().format()}&limit=${limit}&page=${currentPage}`;
            } else {
                if (!bpToken) return { error: 'MISSING_BP_TOKEN' };
                headers['bp-token'] = bpToken;
                requestUrl = `${apiUrl}/transactions?dateFrom=${startMoment.format('YYYY-MM-DDTHH:mm:ss')}&dateTo=${endMoment.format('YYYY-MM-DDTHH:mm:ss')}&size=${limit}&page=${currentPage}`;
            }

            const response = await fetchWithRetry(requestUrl, { method: 'GET', headers });
            
            if (response.status === 401) return { error: 'UNAUTHORIZED' };
            if (!response.ok) throw new Error(`API_ERROR_${response.status}`);

            const data = await response.json();
            const transactionsOnPage = data.data || [];
            allTransactions.push(...transactionsOnPage);

            if (currentPage === 1) {
                const totalItems = data.total || data.total_items || 0;
                if (totalItems === 0) break;
                totalPages = Math.ceil(totalItems / limit);
            }
            currentPage++;
        } while (currentPage <= totalPages);

        // Cálculos
        const approvedSales = allTransactions.filter(t => t.transaction_status === 'aprobada' && t.transaction_type !== 'devolucion');
        const approvedReturns = allTransactions.filter(t => t.transaction_status === 'aprobada' && t.transaction_type === 'devolucion');

        const totalSales = approvedSales.reduce((sum, t) => sum + (Math.abs(parseFloat(getTransactionAmount(t, accountType))) || 0), 0);
        const totalReturns = approvedReturns.reduce((sum, t) => sum + (Math.abs(parseFloat(getTransactionAmount(t, accountType))) || 0), 0);
        
        const netTotal = totalSales - totalReturns;
        const countSales = approvedSales.length;
        const avgTicket = countSales > 0 ? netTotal / countSales : 0;

        return {
            netTotal,
            avgTicket,
            countSales,
            range: {
                from: startMoment.format('DD/MM/YYYY HH:mm'),
                to: endMoment.format('DD/MM/YYYY HH:mm')
            }
        };

    } catch (error) {
        console.error('Summary Service Error:', error);
        return { error: 'FETCH_ERROR', message: error.message };
    }
};

/**
 * Obtiene el resumen mensual
 */
export const fetchMonthlySummary = async (config) => {
    const { accessToken, bpToken, accountType, limitV1, limitV2 } = config;
    const isV2 = accountType !== "Cuenta Actual";
    
    const endMoment = moment.tz('America/Mexico_City');
    const startMoment = moment.tz('America/Mexico_City').startOf('month').subtract(1, 'day').set({ hour: 23, minute: 0, second: 0, millisecond: 0 });

    return await fetchSummaryInRange({
        accessToken, bpToken, accountType,
        startMoment, endMoment,
        limit: isV2 ? limitV2 : limitV1,
        isV2
    });
};

/**
 * Obtiene el resumen diario
 */
export const fetchDailySummary = async (config) => {
    const { accessToken, bpToken, accountType, limitV1, limitV2 } = config;
    const isV2 = accountType !== "Cuenta Actual";

    const endMoment = moment.tz('America/Mexico_City');
    const startMoment = moment.tz('America/Mexico_City').startOf('day');

    return await fetchSummaryInRange({
        accessToken, bpToken, accountType,
        startMoment, endMoment,
        limit: isV2 ? limitV2 : limitV1,
        isV2
    });
};
